import { Dropdown, Label } from "@heroui/react";
import { ChevronDown, LogOut, Menu, Settings } from "lucide-react";
import type { ComponentType, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { logout } from "@/src/auth/store/auth.thunks";
import { LanguageSwitcher } from "@/src/common/components/top-bar/language-switcher";
import { ThemeSwitcher } from "@/src/common/components/top-bar/theme-switcher";
import { Avatar } from "@/src/common/components/ui/avatar";
import { env } from "@/src/common/constants/env";
import { useAppDispatch } from "@/src/common/redux/store";
import { clsx } from "@/src/common/utils/clsx";
import { SETTINGS_ROUTES } from "@/src/settings/settings-routes.constants";

export interface TopBarExtraItem {
	id: string;
	label: string;
	to: string;
	icon?: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
}

interface TopBarProps {
	left?: ReactNode;
	nav?: ReactNode;
	actions?: ReactNode;
	withBrand?: boolean;
	brandMobileOnly?: boolean;
	user?: { name: string; initials?: string; avatarUrl?: string | null };
	hideSettings?: boolean;
	hideUserMenuOnMobile?: boolean;
	extraItems?: readonly TopBarExtraItem[];
	onMenuClick?: () => void;
	className?: string;
	sticky?: boolean;
}

type UserMenuAction = "settings" | "sign-out" | `nav:${string}`;

/**
 * Authed top bar — hamburger (mobile) + optional brand + user dropdown.
 *
 * The search input, school selector, and notifications bell were
 * dead-button placeholders pre-V1; they land with their respective
 * wiring (V1.1). Keeping the top bar lean avoids misleading users.
 */
export function TopBar({
	left,
	nav,
	actions,
	withBrand = false,
	brandMobileOnly = false,
	user,
	hideSettings = false,
	hideUserMenuOnMobile = false,
	extraItems,
	onMenuClick,
	className,
	sticky = false,
}: TopBarProps) {
	const { t } = useTranslation("common");
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const handleAction = (key: React.Key) => {
		const action = String(key) as UserMenuAction;
		if (action === "settings") {
			navigate(SETTINGS_ROUTES.settings);
		} else if (action === "sign-out") {
			void dispatch(logout());
		} else if (action.startsWith("nav:")) {
			const target = extraItems?.find((item) => item.id === action.slice(4));
			if (target) navigate(target.to);
		}
	};

	return (
		<header
			className={clsx(
				"flex h-16 shrink-0 items-center justify-between gap-4 border-b border-border bg-background px-4 md:px-8",
				sticky ? "sticky top-0 z-10" : "",
				className,
			)}
		>
			<div className="flex min-w-0 items-center gap-4 md:gap-6">
				{withBrand ? (
					<Link to="/" className={clsx("flex items-center gap-2.5", brandMobileOnly && "md:hidden")}>
						<img src="/logo-banner.png" alt={env.APP_NAME} className="h-10" />
					</Link>
				) : null}
				{left}
			</div>
			{nav ? <div className="hidden flex-1 items-center justify-center md:flex">{nav}</div> : null}
			<div className="flex items-center gap-3">
				{actions}
				{onMenuClick ? (
					<button
						type="button"
						onClick={onMenuClick}
						aria-label={t("aria.open_menu")}
						className="flex size-9 items-center justify-center rounded-s text-muted-foreground transition-colors hover:bg-surface md:hidden"
					>
						<Menu className="size-4" aria-hidden />
					</button>
				) : null}
				{user ? (
					<Dropdown>
						<Dropdown.Trigger
							aria-label={t("top_bar.user_menu")}
							className={clsx(
								"items-center gap-2 rounded-pill border border-border py-1 pl-1 pr-3 transition-colors hover:bg-surface",
								hideUserMenuOnMobile ? "hidden md:flex" : "flex",
							)}
						>
							<Avatar size="sm" name={user.name} initials={user.initials} src={user.avatarUrl ?? undefined} />
							<span className="hidden text-xs font-medium text-foreground sm:inline">{user.name}</span>
							<ChevronDown className="size-3 text-muted-foreground" aria-hidden />
						</Dropdown.Trigger>
						<Dropdown.Popover placement="bottom end">
							<Dropdown.Menu onAction={handleAction}>
								{extraItems?.map((item) => {
									const Icon = item.icon;
									return (
										<Dropdown.Item key={item.id} id={`nav:${item.id}`} textValue={item.label}>
											{Icon ? <Icon className="size-4" aria-hidden /> : null}
											<Label>{item.label}</Label>
										</Dropdown.Item>
									);
								})}
								{hideSettings ? null : (
									<Dropdown.Item id="settings" textValue={t("nav.settings")}>
										<Settings className="size-4" aria-hidden />
										<Label>{t("nav.settings")}</Label>
									</Dropdown.Item>
								)}
								<Dropdown.Item id="sign-out" textValue={t("top_bar.sign_out")} variant="danger">
									<LogOut className="size-4" aria-hidden />
									<Label>{t("top_bar.sign_out")}</Label>
								</Dropdown.Item>
							</Dropdown.Menu>
							<div className="border-t border-border pt-1">
								<ThemeSwitcher />
								<LanguageSwitcher />
							</div>
						</Dropdown.Popover>
					</Dropdown>
				) : null}
			</div>
		</header>
	);
}
